import { useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useScrollControl } from '@frontend/analytics-shared';
import { ScrollControl } from '@frontend/charts';
import { theme } from '@frontend/theme';
import { NakedUl, useModalControl } from '@frontend/design-system';
import { PhoneTreeCallVolumeChart } from '../../overview-cards/phone-tree-call-volume';
import { PhoneTreePhoneNumbersTrayType } from '../../shared-types';
import { TransformedChartData } from '../../utils/transform-phone-tree-chart-data';
import { PhoneTreeDetailPanel } from '../phone-tree-call-volume-panel';

type Props = {
  data?: TransformedChartData[];
  dataVersion?: number;
};

export const PhoneTreeCarouselView = ({ data, dataVersion = 0 }: Props) => {
  const ulRef = useRef<HTMLUListElement>(null);
  const { modalProps, triggerProps } = useModalControl();
  const { handleControlledScroll, showLeftScrollButton, showRightScrollButton } = useScrollControl<HTMLUListElement>({
    ref: ulRef,
    // we can have only 6 graphs in the horizontal scroll view
    columns: 6,
  });

  const hasLessThan4Items = (data?.length ?? 0) < 4;
  const [modalData, setModalData] = useState<PhoneTreePhoneNumbersTrayType>({
    phoneTreeName: '',
    callRouteId: '',
    callRouteName: '',
    phoneNumbers: [],
  });

  return (
    <>
      <div css={{ position: 'relative' }}>
        <NakedUl css={styles.ul} ref={ulRef}>
          {data?.map(({ phoneTreeId, ...rest }) => {
            return (
              <PhoneTreeCallVolumeChart
                key={`${phoneTreeId}-${dataVersion}`}
                phoneTreeId={phoneTreeId}
                triggerProps={triggerProps}
                setModalData={setModalData}
                style={{
                  flex: 1,
                  width: 'auto',
                  margin: 0,
                  minWidth: hasLessThan4Items ? 0 : '400px',
                }}
                {...rest}
              />
            );
          })}
        </NakedUl>
        <ScrollControl
          css={[styles.scrollButton, showLeftScrollButton ? styles.scrollButtonVisible : styles.scrollButtonInVisible]}
          direction='left'
          disabled={!showLeftScrollButton}
          isVisible={showLeftScrollButton && !hasLessThan4Items}
          onClick={() => handleControlledScroll('left')}
        />

        <ScrollControl
          css={[styles.scrollButton, showRightScrollButton ? styles.scrollButtonVisible : styles.scrollButtonVisible]}
          direction='right'
          disabled={!showRightScrollButton}
          isVisible={showRightScrollButton && !hasLessThan4Items}
          onClick={() => handleControlledScroll('right')}
        />
      </div>
      {modalProps.show && <PhoneTreeDetailPanel onClose={modalProps.onClose} modalData={modalData} />}
    </>
  );
};

const styles = {
  ul: css`
    display: flex;
    gap: ${theme.spacing(2)};
    overflow: auto;
  `,
  scrollButton: css`
    bottom: 0;
    margin: auto;
    position: absolute;
    top: 0;
    height: auto;
    width: ${theme.spacing(3)};
    padding: ${theme.spacing(0)};

    &.left-scroll {
      left: 0px;
    }

    &.right-scroll {
      right: 0px;
    }
  `,
  scrollButtonVisible: css`
    box-shadow: ${theme.shadows.floating};
  `,
  scrollButtonInVisible: css`
    box-shadow: none;
  `,
};
